import { gql, useMutation, useQuery } from '@apollo/client'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'

import {
  ArtistUpdateArtistProfileMutation,
  ArtistUpdateArtistProfileMutationVariables,
  ConsoleMyProfileEditQuery,
  ConsoleMyProfileEditQueryVariables,
  UpdateArtistProfileTextMutation,
  UpdateArtistProfileTextMutationVariables,
  UpdateArtistSnsMutation,
  UpdateArtistSnsMutationVariables,
  UpdateArtistTextLinkMutation,
  UpdateArtistTextLinkMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useCurrentUser,
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Camera } from '@/core/native'

import { Action, fragments, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages } = useRouter()
  const { t } = useTranslation()
  const { showError } = useMessage()
  const { getImage } = Camera
  const { upload } = useUserTmpUploader(user?.uid ?? '')
  const { data } = useQuery<
    ConsoleMyProfileEditQuery,
    ConsoleMyProfileEditQueryVariables
  >(QUERY)

  const [artistUpdateArtistProfile] = useMutation<
    ArtistUpdateArtistProfileMutation,
    ArtistUpdateArtistProfileMutationVariables
  >(ARTIST_UPDATE_ARTIST_PROFILE)

  const [updateArtistSns] = useMutation<
    UpdateArtistSnsMutation,
    UpdateArtistSnsMutationVariables
  >(UPDATE_ARTIST_SNS)

  const [updateArtistProfileText] = useMutation<
    UpdateArtistProfileTextMutation,
    UpdateArtistProfileTextMutationVariables
  >(UPDATE_ARTIST_PROFILE_TEXT)

  const [updateArtistTextLink] = useMutation<
    UpdateArtistTextLinkMutation,
    UpdateArtistTextLinkMutationVariables
  >(UPDATE_ARTIST_TEXT_LINK)

  return {
    state: {
      artistId: user?.uid ?? '',
      user: data?.artist ?? null,
    },
    actions: {
      async onSubmitMyProfile(values) {
        const snsData = mapValues(keyBy(values.sns, 'type'), 'url')
        await updateArtistSns({
          variables: { inputs: snsData },
          //not need save cache
          fetchPolicy: 'no-cache',
        })

        await updateArtistProfileText({
          variables: {
            profileText: values.introduction.length
              ? values.introduction
              : null,
          },
          //not need save cache
          fetchPolicy: 'no-cache',
        })

        await updateArtistTextLink({
          variables: { inputs: values.textLink ?? [] },
          //not need save cache
          fetchPolicy: 'no-cache',
        })

        await artistUpdateArtistProfile({
          variables: {
            inputs: {
              displayName: values.displayName,
              accountId: values.accountId,
              introduction: values.introduction,
              avatarPath: values.avatarPath,
            },
          },
        })
        await router.goBack(routerPages.ConsoleAccountSetting)
      },
      onClickAvatar() {
        return getImage(config.upload.mimeTypes.image)
          .then(file => {
            if (file.size > config.upload.limit['image']) {
              throw new Error('uploadErrorMaximumSize')
            }
            return upload(file)
          })
          .catch(error => {
            if (error.message === 'uploadErrorMaximumSize') {
              showError(new Error(t('uploadErrorMaximumSize')))
              return
            }
            if (error.message === 'uploadErrorImageOnly') {
              showError(new Error(t('uploadErrorImageOnly')))
              return
            }
            showError(error)
            return
          })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleMyProfileEdit($artistId: ID) {
    artist(id: $artistId) {
      ...ConsoleMyProfileConnection
    }
  }
  ${fragments.consoleMyProfile}
`

const ARTIST_UPDATE_ARTIST_PROFILE = gql`
  mutation ArtistUpdateArtistProfile($inputs: ArtistUpdateArtistProfileInput!) {
    artistUpdateArtistProfile(inputs: $inputs) {
      ...ConsoleMyProfileConnection
    }
  }
  ${fragments.consoleMyProfile}
`

const UPDATE_ARTIST_SNS = gql`
  mutation UpdateArtistSns($inputs: UpdateArtistSnsInput!) {
    updateArtistSns(inputs: $inputs) {
      ...ConsoleMyProfileConnection
    }
  }
  ${fragments.consoleMyProfile}
`

const UPDATE_ARTIST_PROFILE_TEXT = gql`
  mutation UpdateArtistProfileText($profileText: String) {
    updateArtistProfileText(profileText: $profileText) {
      ...ConsoleMyProfileConnection
    }
  }
  ${fragments.consoleMyProfile}
`

const UPDATE_ARTIST_TEXT_LINK = gql`
  mutation UpdateArtistTextLink($inputs: [UpdateArtistTextLinkInput]!) {
    updateArtistTextLink(inputs: $inputs) {
      ...ConsoleMyProfileConnection
    }
  }
  ${fragments.consoleMyProfile}
`
